import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import {
  BookOpenIcon,
  Code2Icon,
  UserCircleIcon,
  SquareStackIcon,
  ImageIcon,
  TypeIcon,
  ItalicIcon,
  AlignLeftIcon,
  Heading1Icon,
  LinkIcon,
  LayoutGridIcon,
  TagIcon,
  TagsIcon,
  HammerIcon,
  Grid2x2Icon,
  FileTextIcon,
  RocketIcon,
  LightbulbIcon,
  PaletteIcon,
  ShieldIcon,
  CircleHelpIcon,
  ArrowLeftRightIcon,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { parseBML } from "@/lib/bml-parser";
import { renderBML } from "@/lib/bml-renderer";
import { Button } from "@/components/ui/button";
import { PanelLeftIcon } from "lucide-react";
import { useDocsSidebarStore } from "@/stores/docsSidebarStore";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/docs")({
  head: () => ({
    title: "clove.moe docs - get started",
    meta: [
      {
        name: "description",
        content:
          "learn how to make your clove.moe profile: quick start, tips, safety, faq, and the bml reference",
      },
      { property: "og:title", content: "clove.moe docs" },
      {
        property: "og:description",
        content: "simple docs for clove.moe with the bml reference",
      },
    ],
  }),
  component: DocsPage,
});

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

function DocsSidebarTrigger({
  className,
  onClick,
  iconSize,
  ...props
}: React.ComponentProps<typeof Button> & { iconSize?: number }) {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      onClick={(e) => {
        onClick?.(e);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeftIcon
        className={cn("h-6 w-6", iconSize && `h-${iconSize} w-${iconSize}`)}
      />
      <span className="sr-only">toggle sidebar</span>
    </Button>
  );
}

// syncs the sidebar context into the zustand store for global reads
function DocsSidebarStoreSync() {
  const { open, openMobile, isMobile, state } = useSidebar();
  const setSidebar = useDocsSidebarStore((s) => s.setSidebar);

  useEffect(() => {
    setSidebar({
      isOpen: isMobile ? openMobile : open,
      isMobile,
      state,
    });
  }, [open, openMobile, isMobile, state, setSidebar]);

  return null;
}

function DocsPage() {
  const navigate = useNavigate();

  // on deep links (/docs#faq), scroll to the target once, then rewrite the
  // hash with hash scrolling disabled so the router stops re-scrolling to
  // it on later transitions (which yanks the page back while scrolling)
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    document.getElementById(id)?.scrollIntoView();
    void navigate({
      hash: id,
      replace: true,
      hashScrollIntoView: false,
      resetScroll: false,
    });
  }, [navigate]);

  const sample = useMemo(
    () =>
      `
  <Bio>
    <Header>
      <Name className="text-2xl">luna</Name>
      <Title>builder and tea enjoyer</Title>
    </Header>
    <Section title="links">
      <LinkGrid>
        <Link href="#">twitter</Link>
        <Link href="#">github</Link>
      </LinkGrid>
    </Section>
  </Bio>
  `.trim(),
    []
  );

  const sampleRendered = useMemo(() => {
    const result = parseBML(sample);
    if (result.error || !result.ast) return null;
    return renderBML(result.ast);
  }, [sample]);

  const componentIds = [
    { id: "bio", label: "bio" },
    { id: "header", label: "header" },
    { id: "section", label: "section" },
    { id: "image", label: "image" },
    { id: "name", label: "name" },
    { id: "title", label: "title" },
    { id: "text", label: "text" },
    { id: "heading", label: "heading" },
    { id: "link", label: "link" },
    { id: "linkgrid", label: "linkgrid" },
    { id: "badge", label: "badge" },
    { id: "badgegroup", label: "badgegroup" },
    { id: "skill", label: "skill" },
    { id: "skillgrid", label: "skillgrid" },
  ];

  // fonts guide section text used in both beginner and advanced contexts
  const FontsNote = (
    <div className="mt-6 rounded-lg border border-primary/40 bg-primary/5 p-4">
      <div className="text-sm text-muted-foreground space-y-2">
        <p>you can use your uploaded fonts in two ways:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            bml mode: set the <code className="font-mono text-xs">font</code>{" "}
            prop on the text components (
            <code className="font-mono text-xs">Name</code>,{" "}
            <code className="font-mono text-xs">Title</code>,{" "}
            <code className="font-mono text-xs">Heading</code>,{" "}
            <code className="font-mono text-xs">Text</code>). use a url such as{" "}
            <code className="font-mono text-xs">
              https://cdn.clove.moe/your-font.woff2
            </code>
          </li>
          <li>
            advanced html/css mode: open the media manager. use the{" "}
            <span className="font-mono text-xs">font</span> action to put an
            <span className="font-mono text-xs">@font-face</span> rule into your
            css
          </li>
        </ul>
        <p className="text-xs">
          note: for safety, font urls must come from our cdn. the system ignores
          urls from other sources
        </p>
      </div>
    </div>
  );

  const isDesktop = useMediaQuery("(min-width: 768px)");
  const sidebarVariant = isDesktop ? "inset" : "floating";
  const sidebarCollapsible = isDesktop ? "icon" : "offcanvas";

  return (
    <SidebarProvider>
      <DocsSidebarStoreSync />
      <DocsSidebarNav
        componentIds={componentIds}
        variant={sidebarVariant}
        collapsible={sidebarCollapsible}
      />
      <SidebarInset>
        <div className="md:hidden sticky top-0 z-20 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="px-4 py-2 flex items-center gap-2">
            <DocsSidebarTrigger className="-ml-2" iconSize={5} />
          </div>
        </div>

        <div id="welcome" className="px-4 sm:px-6 lg:px-10 py-10">
          <div className="max-w-3xl">
            <img
              src="/header-logo.png"
              alt="clove.moe"
              className="h-16 w-auto mb-6"
            />

            <section className="scroll-mt-24">
              <h1 className="text-3xl font-bold tracking-tight">
                welcome to clove.moe
              </h1>
              <div className="mt-4 space-y-3 text-[15px] leading-7 text-muted-foreground">
                <p>
                  clove.moe is a platform that lets you make a personal bio
                  page. you write the page in a simple markup language. the name
                  of this language is bml. you have full control of the layout
                  and the content of your page.
                </p>
                <p>
                  other bio link tools give you only fixed templates. with bml
                  (bio markup language), you can make the page that you want.
                  you do not need code knowledge. bml is easy to learn.
                </p>
                <p>
                  this guide tells you how to make your first profile. it also
                  gives a description of each component that you can use.
                </p>
              </div>
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <section
              id="getting-started"
              className="scroll-mt-24"
            >
              <h2 className="text-2xl font-semibold tracking-tight">getting started</h2>
              <div className="mt-4 space-y-4 text-[15px] leading-7">
                <p className="text-muted-foreground">
                  do these steps to make your first clove.moe profile:
                </p>
                <ol className="list-decimal pl-6 space-y-3">
                  <li>
                    <strong>create your account:</strong> click "sign up".
                    select a username that is not in use. the username becomes
                    part of your profile url{" "}
                    {`(clove.moe/profile/<yourusername>)`}. select the username
                    carefully.
                  </li>
                  <li>
                    <strong>open the editor:</strong> sign in. then click
                    "create" or "editor" to open the bml editor. you write your
                    profile in this editor.
                  </li>
                  <li>
                    <strong>start with the basics:</strong> each profile starts
                    with a Bio component. add your name with the Name component.
                    add a short description with the Title component. you can
                    change the content at all times.
                  </li>
                  <li>
                    <strong>add your links:</strong> use Section components to
                    divide your content. use a LinkGrid with Link components to
                    show your social media, your website, or other links.
                  </li>
                  <li>
                    <strong>preview as you go:</strong> use the preview toggle
                    to see your profile. the editor shows the changes
                    immediately.
                  </li>
                  <li>
                    <strong>claim your url:</strong> click save. then claim your
                    url. your profile is then live at{" "}
                    {`clove.moe/<yourlinkhere>`}.
                  </li>
                  <li>
                    <strong>publish:</strong> set the publish switch to on to
                    make your profile visible to other users. set the switch to
                    off to make the profile private again.
                  </li>
                </ol>
              </div>
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <section
              id="tips"
              className="scroll-mt-24"
            >
              <h2 className="text-2xl font-semibold tracking-tight">beginner tips</h2>
              <div className="mt-4 space-y-4 text-[15px] leading-7">
                <p className="text-muted-foreground">
                  these tips can make your clove.moe profile better:
                </p>
                <ul className="list-disc pl-6 space-y-3">
                  <li>
                    <strong>start simple, then expand:</strong> at the start,
                    add only your name, a short title, and 2 or 3 important
                    links. add more sections, for example skills or badges,
                    later. a short and clean profile is easier to read than a
                    full profile.
                  </li>
                  <li>
                    <strong>be consistent with styling:</strong> if you use
                    title case for one link ("My Portfolio"), use it for all
                    links. also keep the same spacing and punctuation for all
                    links. one style makes your profile look more professional.
                  </li>
                  <li>
                    <strong>prioritize your most important links:</strong> put
                    your 3 to 6 most important links first. examples are your
                    portfolio, your primary social media, or your contact
                    information. many visitors do not scroll through long lists.
                  </li>
                  <li>
                    <strong>use sections to organize content:</strong> do not
                    put all your content in one long list. divide the content
                    into sections, for example "social", "work", "projects", or
                    "contact". sections make your profile easy to scan.
                  </li>
                  <li>
                    <strong>write clear, descriptive link labels:</strong> write
                    "follow me on twitter" or "twitter updates", not only
                    "twitter". a clear label tells visitors what the link opens.
                  </li>
                  <li>
                    <strong>test on mobile:</strong> many visitors open your
                    profile on a phone. use the preview to see your profile on a
                    small screen. change the layout if it is necessary.
                  </li>
                </ul>
              </div>
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <section
              id="custom-fonts"
              className="scroll-mt-24"
            >
              <h2 className="text-2xl font-semibold tracking-tight">using custom fonts</h2>
              <div className="mt-4 space-y-4 text-[15px] leading-7">
                <p className="text-muted-foreground">
                  you can upload custom fonts and use them on your profile.
                  custom fonts change the typography of your page. they make
                  your profile different from other profiles.
                </p>
                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-foreground">
                      getting started with custom fonts:
                    </h3>
                    <p className="text-muted-foreground">
                      first, upload your font files (.woff2, .woff, .ttf, or
                      .otf) with the media manager in the editor. all these
                      formats are permitted. we recommend .woff2 because it has
                      the best performance. after the upload, our cdn holds your
                      fonts and loads them quickly.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      using fonts in bml mode:
                    </h3>
                    <p className="text-muted-foreground">
                      each text component (Name, Title, Heading, or Text) has a{" "}
                      <code className="font-mono text-xs">font</code> prop. set
                      the prop to the cdn url of your font. the font then
                      applies to that component only. thus you can use one font
                      for your name and a different font for body text.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      using fonts in advanced html/css mode:
                    </h3>
                    <p className="text-muted-foreground">
                      open the media manager. find your font. click the font
                      action button. this puts a correct @font-face rule, with
                      the correct cdn url, into your css. then you can use the
                      font family name in your custom css.
                    </p>
                  </div>
                </div>
              </div>
              {FontsNote}
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <section
              id="safety"
              className="scroll-mt-24"
            >
              <h2 className="text-2xl font-semibold tracking-tight">safety and moderation</h2>

              <div className="mt-4 space-y-4 text-[15px] leading-7">
                <p className="text-muted-foreground">
                  clove.moe must be safe for all users. content that can cause
                  harm to other users is not permitted.
                </p>
                <p className="text-muted-foreground">
                  examples of content that is not permitted:
                </p>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>harassment, hate speech, or discrimination</li>
                  <li>
                    spam, low-quality content, or content that causes disruption
                  </li>
                  <li>links or content that can cause harm to visitors</li>
                  <li>adult content or links to unsafe websites</li>
                  <li>
                    copyrighted content that you do not have permission to use
                  </li>
                </ul>

                <div>
                  <h3 className="font-medium text-foreground">
                    what happens if something gets reported:
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    when we receive a report, we examine the content. if there
                    is a problem, we can hide your profile for a short time
                    while we examine it. for serious problems, we can suspend
                    the account. we will always tell you what occurs. if it is
                    possible, we will give you time to correct the problem.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-foreground">
                    built-in safety features:
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    bml has these safety features:
                  </p>
                  <ul className="mt-2 list-disc pl-6 space-y-1 text-muted-foreground">
                    <li>
                      you must upload images with our media manager (external
                      images are not permitted)
                    </li>
                    <li>
                      links operate only with safe protocols, for example https
                      and mailto
                    </li>
                    <li>the system blocks dangerous code features</li>
                    <li>
                      the system does a security check on all content before the
                      content goes live
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-medium text-foreground">
                    how to report something:
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    if you see content that is not correct, tell us. use the
                    report link at the bottom of each profile, or speak to our
                    support team. all reports are private. we usually reply in
                    24 to 72 hours.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-foreground">
                    think we made a mistake?
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    if we removed your content and you think this was an error,
                    you can send an appeal to support. tell us your username and
                    the reason for the appeal. we will examine the content again
                    in 24 to 72 hours.
                  </p>
                </div>
              </div>
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <section
              id="faq"
              className="scroll-mt-24"
            >
              <h2 className="text-2xl font-semibold tracking-tight">
                frequently asked questions
              </h2>
              <div className="mt-4 text-[15px] leading-7 space-y-4">
                <div>
                  <div className="font-medium text-foreground">
                    can i customize the appearance of my profile?
                  </div>
                  <div className="text-muted-foreground mt-1">
                    yes. each bml component has a className prop. use tailwind
                    css utilities in this prop. for example:{" "}
                    <code className="bg-muted/40 px-1 rounded">
                      className="text-blue-500 font-bold"
                    </code>{" "}
                    makes the text blue and bold. you can change colors, sizes,
                    spacing, and more.
                  </div>
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    what if i make a mistake in my bml?
                  </div>
                  <div className="text-muted-foreground mt-1">
                    the editor shows errors with red underlines and messages.
                    usual errors are: a missing closing tag, an incorrect
                    component name, or an incorrect prop format. correct the
                    errors that the editor shows. your profile will then render
                    correctly.
                  </div>
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    can i add images to my profile?
                  </div>
                  <div className="text-muted-foreground mt-1">
                    yes. use the Image component for profile pictures. for
                    security, you must upload images with the media manager in
                    the editor. our cdn (cdn.clove.moe) holds the images. links
                    to external images do not operate.
                  </div>
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    how do i organize my links and content?
                  </div>
                  <div className="text-muted-foreground mt-1">
                    use Section components to keep related content together. for
                    example, make different sections for "social media",
                    "work", and "projects". in a section, use a LinkGrid for
                    many links, or use one Link component for one item.
                  </div>
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    can i see how my profile looks before publishing?
                  </div>
                  <div className="text-muted-foreground mt-1">
                    yes. the editor has a preview toggle. the preview shows your
                    profile as visitors will see it. you can change between edit
                    mode and preview mode at all times. your profile is visible
                    to other users only after you publish it.
                  </div>
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    what happens if i want to change my username on my profile
                    later?
                  </div>
                  <div className="text-muted-foreground mt-1">
                    no. you cannot change a username after signup. select your
                    username carefully. to use a different username, you must
                    make a new account. a username change function is possible
                    in the future.
                  </div>
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    what about if i want to change the name of any claimed links
                    later?
                  </div>
                  <div className="text-muted-foreground mt-1">
                    no. you cannot change the name of a claimed link. to use a
                    different name, you must make a new link and configure it
                    again.
                  </div>
                </div>

                <div>
                  <div className="font-medium text-foreground">
                    how do i report inappropriate content?
                  </div>
                  <div className="text-muted-foreground mt-1">
                    if you find content that does not obey our community
                    guidelines, use the report link in the footer of the profile
                    page. as an alternative, speak to our support team. we
                    examine all reports quickly.
                  </div>
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    is there a limit to how much content i can add?
                  </div>
                  <div className="text-muted-foreground mt-1">
                    there is no strict limit on characters. but we recommend a
                    short profile that is easy to scan. a very long profile is
                    not good for visitors. quality is more important than
                    quantity.
                  </div>
                </div>
              </div>
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <section
              id="link-transfers"
              className="scroll-mt-24"
            >
              <h2 className="text-2xl font-semibold tracking-tight">transferring a link</h2>
              <div className="mt-4 space-y-4 text-[15px] leading-7">
                <p className="text-muted-foreground">
                  you can move the ownership of one of your links (pages) to a
                  different user. to do this, you use a secret code that
                  operates one time only. this is useful when you change your
                  brand, give a project to a teammate, or move a name to a
                  different account. transfers are safe: the code operates only
                  for the correct recipient. the code expires after 7 days. you
                  can cancel the code at all times before the recipient claims
                  it.
                </p>
                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-foreground">key facts:</h3>
                    <ul className="mt-2 list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>
                        only the current owner of a page can start a transfer
                      </li>
                      <li>
                        each code starts with{" "}
                        <code className="px-1 bg-muted/40 rounded text-xs">
                          clv-
                        </code>{" "}
                        and operates one time only
                      </li>
                      <li>a code expires after 7 days if it is not claimed</li>
                      <li>
                        you cannot change the recipient after you make the code
                        - cancel the code and start again
                      </li>
                      <li>
                        when the recipient claims the code, the ownership moves
                        immediately (the page content does not change)
                      </li>
                      <li>
                        if the ownership changes before the claim, the claim
                        will fail with a conflict
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      how to initiate (sender):
                    </h3>
                    <ol className="mt-2 list-decimal pl-6 space-y-2 text-muted-foreground">
                      <li>
                        go to your dashboard. click the 3 dots at the right of
                        the applicable link
                      </li>
                      <li>
                        select{" "}
                        <span className="font-medium">transfer ownership</span>
                      </li>
                      <li>
                        find the recipient by username, display name, or name.
                        select the recipient
                      </li>
                      <li>
                        click <span className="font-medium">generate code</span>
                      </li>
                      <li>
                        copy the code. send the code to the recipient in a
                        private message
                      </li>
                      <li>
                        the transfer shows as pending until the recipient claims
                        it or you cancel it
                      </li>
                    </ol>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      how to cancel (sender):
                    </h3>
                    <p className="text-muted-foreground">
                      while the code shows, click{" "}
                      <span className="font-medium">need someone else?</span> to
                      cancel. this deletes the pending record. the code then
                      stops immediately.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      how to claim (recipient):
                    </h3>
                    <ol className="mt-2 list-decimal pl-6 space-y-2 text-muted-foreground">
                      <li>
                        go to your dashboard. click the{" "}
                        <span className="font-medium">transfer a link</span>{" "}
                        button
                      </li>

                      <li>
                        put the code in the field. the code must start with{" "}
                        <code className="px-1 bg-muted/40 rounded text-xs">
                          clv-
                        </code>
                        . then continue
                      </li>
                      <li>
                        examine the details of the link owner, the recipient,
                        and the page
                      </li>
                      <li>
                        click{" "}
                        <span className="font-medium">confirm and claim</span>{" "}
                        to complete the transfer
                      </li>
                    </ol>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      troubleshooting:
                    </h3>
                    <ul className="mt-2 list-disc pl-6 space-y-1 text-muted-foreground">
                      <li>
                        <code className="px-1 bg-muted/40 rounded text-xs">
                          invalid code
                        </code>
                        : the code has an error, or the{" "}
                        <code className="px-1 bg-muted/40 rounded text-xs">
                          clv-
                        </code>{" "}
                        prefix is missing
                      </li>
                      <li>
                        <code className="px-1 bg-muted/40 rounded text-xs">
                          forbidden
                        </code>
                        : you are not the correct recipient account
                      </li>
                      <li>
                        <code className="px-1 bg-muted/40 rounded text-xs">
                          code already used
                        </code>
                        : the code was claimed before - ask the sender for a
                        new code
                      </li>
                      <li>
                        <code className="px-1 bg-muted/40 rounded text-xs">
                          code expired
                        </code>
                        : the sender must start a new transfer
                      </li>
                      <li>
                        <code className="px-1 bg-muted/40 rounded text-xs">
                          conflict
                        </code>
                        : the ownership changed before the claim - start the
                        transfer again
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      keep it safe:
                    </h3>
                    <p className="text-muted-foreground">
                      send the code only to the correct person (private message,
                      email). each person who has the code can take the
                      ownership immediately.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <section
              id="bml-overview"
              className="scroll-mt-24"
            >
              <h1 className="text-3xl font-bold tracking-tight">
                what is bml?
              </h1>
              <div className="mt-4 space-y-4 text-[15px] leading-7">
                <p className="text-muted-foreground">
                  bml (bio markup language) is a simple language for your
                  profile. bml is easier than html but has more functions than
                  plain text. bml gives you control without complexity.
                </p>
                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-foreground">
                      why bml exists:
                    </h3>
                    <p className="text-muted-foreground">
                      most bio link tools give you only fixed templates. with
                      bml, you can make the page that you want. bml stays easy
                      for new users. you get the flexibility of custom code, but
                      you do not write code.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      how it works:
                    </h3>
                    <p className="text-muted-foreground">
                      bml uses components. a component is a building block. you
                      connect components to make your profile. each component
                      has one function - Name shows your display name, Link
                      makes a link, Section puts content into groups.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      the learning curve:
                    </h3>
                    <p className="text-muted-foreground">
                      if you know html, bml is familiar. if you do not know
                      html, that is not a problem. most persons can make a first
                      profile in less than 10 minutes.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  quick example
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <pre className="rounded-lg border bg-muted/40 p-4 text-[13px] leading-6 overflow-auto">
                    {`<Bio>
  <Header>
    <Name className="text-2xl">luna</Name>
    <Title>builder and tea enjoyer</Title>
  </Header>
  <Section title="links">
    <LinkGrid>
      <Link href="#">twitter</Link>
      <Link href="#">github</Link>
    </LinkGrid>
  </Section>
</Bio>`}
                  </pre>
                  <div className="rounded-lg border bg-background p-3 text-sm">
                    {sampleRendered}
                  </div>
                </div>
              </div>
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <section
              id="bml-syntax"
              className="scroll-mt-24"
            >
              <h2 className="text-2xl font-semibold tracking-tight">bml syntax basics</h2>
              <div className="mt-4 space-y-4 text-[15px] leading-7">
                <p className="text-muted-foreground">
                  bml syntax has these primary concepts:
                </p>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-foreground">
                      components (the building blocks):
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      components are containers for your content. a component
                      name always starts with a capital letter and has angle
                      brackets around it:
                    </p>
                    <code className="block bg-muted/40 p-2 rounded text-xs">
                      {"<Bio>"} - the main container for your entire profile
                      <br />
                      {"<Name>"} - displays your name
                      <br />
                      {"<Link>"} - creates a clickable link
                    </code>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      props (component settings):
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      props control how a component operates. text props use
                      quotation marks. number props use curly braces:
                    </p>
                    <code className="block bg-muted/40 p-2 rounded text-xs">
                      {`<Section title="about me"> - text prop with quotes`}
                      <br />
                      {`<Skill level={85}> - number prop with braces`}
                    </code>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      styling with className:
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      use the className prop with tailwind css to change the
                      appearance of a component{" "}
                      <sup className="text-[9px] super font-medium text-muted-foreground">
                        <a href="#tailwind-basics">(what is this?)</a>
                      </sup>
                      :
                    </p>
                    <code className="block bg-muted/40 p-2 rounded text-xs">
                      {`<Name className="text-3xl font-bold">your name</Name>`}
                      <br />
                      {`<Link className="bg-blue-500 text-white">my link</Link>`}
                    </code>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      nesting components:
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      a component can contain other components, the same as
                      boxes in boxes:
                    </p>
                    <code className="block bg-muted/40 p-2 rounded text-xs">
                      {`<Section title="links">
  <LinkGrid>
    <Link href="#">twitter</Link>
    <Link href="#">github</Link>
  </LinkGrid>
</Section>`}
                    </code>
                  </div>
                </div>
              </div>
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <section
              id="tailwind-basics"
              className="scroll-mt-24"
            >
              <h2 className="text-2xl font-semibold tracking-tight">
                styling with tailwind css
              </h2>
              <div className="mt-4 space-y-4 text-[15px] leading-7">
                <p className="text-muted-foreground">
                  tailwind css is a css framework that uses utility classes.
                  you add class names to style elements. tailwind is the
                  primary method to style components in bml. tailwind is
                  different from usual css, but it is powerful and easy to use.
                </p>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-foreground">
                      how tailwind works:
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      each class does one thing. for blue text, use{" "}
                      <code className="bg-muted/40 px-1 rounded">
                        text-blue-500
                      </code>
                      . for larger text, use{" "}
                      <code className="bg-muted/40 px-1 rounded">text-xl</code>.
                      connect classes to get the style that you want.
                    </p>
                  </div>

                  <div className="max-w-xl">
                    <h3 className="font-medium text-foreground">
                      visual example:
                    </h3>
                    <p className="text-muted-foreground mb-3">
                      this example shows a basic component and the same
                      component with tailwind classes:
                    </p>
                    <div className="space-y-4 mb-2">
                      <p className="font-medium text-foreground">
                        without styling:
                      </p>
                      <code className="block bg-muted/40 p-3 text-xs rounded">
                        {`<Name>alex chen</Name>`}
                      </code>
                      <div className="border rounded-lg p-3 bg-background">
                        <div className="text-base">alex chen</div>
                      </div>
                    </div>
                    <div className="space-y-4 mb-2">
                      <p className="font-medium text-foreground">
                        with tailwind styling:
                      </p>
                      <code className="block bg-muted/40 p-3 text-xs rounded">
                        {`<Name className="text-3xl font-bold text-blue-600">alex chen</Name>`}
                      </code>
                      <div className="border rounded-lg p-3 bg-background">
                        <div className="text-3xl font-bold text-blue-600">
                          alex chen
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground mt-4">
                      the styled version uses:{" "}
                      <code className="bg-muted/40 px-1 rounded">text-3xl</code>{" "}
                      (large text),{" "}
                      <code className="bg-muted/40 px-1 rounded">
                        font-bold
                      </code>{" "}
                      (bold weight),{" "}
                      <code className="bg-muted/40 px-1 rounded">
                        text-blue-600
                      </code>{" "}
                      (blue color).
                    </p>
                  </div>

                  <div className="mt-4">
                    <h3 className="font-medium text-foreground mb-2">
                      tips for beginners:
                    </h3>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>
                        start simple - add one class at a time and see the
                        change
                      </li>
                      <li>
                        use the preview toggle to see your changes immediately
                      </li>
                      <li>
                        the numbers in classes usually go from 1 (smallest) to
                        12 or more (largest)
                      </li>
                      <li>
                        the hover: prefix applies a style only when the pointer
                        is on the element
                      </li>
                      <li>
                        you can connect many classes - put a space between the
                        classes
                      </li>
                      <li>
                        if the style is not correct, remove the classes one at a
                        time to find the problem
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-medium text-foreground">
                      want to learn more?
                    </h3>
                    <p className="text-muted-foreground">
                      this section shows only the basics. tailwind has hundreds
                      of utility classes. for a full reference of all the
                      classes, see the{" "}
                      <a
                        href="https://tailwindcss.com/docs/styling-with-utility-classes"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        official tailwind css documentation
                      </a>
                      . the documentation has a visual example for each utility
                      class.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <div className="my-12">
              <Separator className="bg-border/60" />
            </div>

            <ComponentDoc id="bio" title="<Bio>">
              <p>
                the primary container for your full profile. each clove.moe
                profile must start and end with the Bio tags. Bio holds all the
                other components.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> always. Bio is necessary as the
                outermost component of each profile.
              </p>
              <Props
                items={[
                  {
                    name: "className",
                    desc: "optional tailwind classes for custom styling",
                  },
                ]}
              />
              <Code>{`<Bio>
  <!-- all your profile content goes here -->
  <Header>...</Header>
  <Section>...</Section>
</Bio>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="header" title="<Header>">
              <p>
                a container for your name and title at the top of your profile.
                Header makes a clean introduction section.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use Header to show your name and
                title together. usually, put Header directly after the opening
                Bio tag.
              </p>
              <Props
                items={[
                  {
                    name: "className",
                    desc: "optional tailwind classes for custom styling",
                  },
                ]}
              />
              <Code>{`<Header>
  <Name>luna</Name>
  <Title>builder and tea enjoyer</Title>
</Header>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="section" title="<Section>">
              <p>
                puts your content into groups. a group can have a heading. use
                Section to divide different types of information, for example
                "social links", "work", or "about me".
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use Section to keep related
                content together. sections make your profile easy to scan.
              </p>
              <Props
                items={[
                  {
                    name: "title",
                    desc: "optional heading text that appears above the section content",
                  },
                  {
                    name: "className",
                    desc: "optional tailwind classes for custom styling",
                  },
                ]}
              />
              <Code>{`<Section title="social links">
  <LinkGrid>
    <Link href="#">twitter</Link>
    <Link href="#">github</Link>
  </LinkGrid>
</Section>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="image" title="<Image>">
              <p>
                shows a profile image or an avatar. you can put an Image in
                your header or in other locations on your profile. the system
                automatically optimizes the images. our cdn sends the images
                quickly.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use Image for a profile picture
                in your header, for artwork, or for other visual content.
              </p>
              <p className="text-xs">
                <strong>note:</strong> for security, you must upload images
                with our media manager. our cdn (<code>cdn.clove.moe</code>)
                holds the images. external image urls do not render.
              </p>
              <Props
                items={[
                  {
                    name: "src",
                    desc: "image url from cdn.clove.moe (required)",
                  },
                  {
                    name: "size",
                    desc: "'sm' | 'md' | 'lg' controls the display size (default: md)",
                  },
                  {
                    name: "className",
                    desc: "optional tailwind classes for additional styling",
                  },
                ]}
              />
              <Code>{`<Image src="https://cdn.clove.moe/me.png" size="lg" />`}</Code>
            </ComponentDoc>

            <ComponentDoc id="name" title="<Name>">
              <p>
                shows your primary display name - the most prominent text on
                your profile. this text is your introduction to visitors.
                usually, Name is at the top of your profile, in a Header
                component.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> always put Name in your header.
                show your name, your username, or your brand. apply a style
                that gets attention.
              </p>
              <Props
                items={[
                  {
                    name: "className",
                    desc: "optional tailwind classes that change the size, color, or weight",
                  },
                  {
                    name: "font",
                    desc: "cdn font url that applies a custom font to your name only (https://cdn.clove.moe/...)",
                  },
                ]}
              />
              <Code>{`<Name font="https://cdn.clove.moe/my-font.woff2" className="text-3xl font-bold">luna</Name>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="title" title="<Title>">
              <p>
                shows a short tagline, role, or description below your name.
                use Title to tell visitors what you do. keep the text short -
                for example "builder and tea enjoyer" or "designer at acme
                corp". do not write a full bio in Title.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use Title with Name in a Header
                to make a full introduction. visitors then immediately know who
                you are.
              </p>
              <Props
                items={[
                  {
                    name: "className",
                    desc: "optional tailwind classes for styling",
                  },
                  {
                    name: "font",
                    desc: "cdn font url to apply a custom font (https://cdn.clove.moe/...)",
                  },
                ]}
              />
              <Code>{`<Title font="https://cdn.clove.moe/my-font.woff" className="text-muted-foreground">builder and designer</Title>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="text" title="<Text>">
              <p>
                shows paragraph text. use Text for descriptions, bios,
                explanations, or other body copy. the variant prop controls the
                emphasis - use default for standard text, use muted for text
                that is less important.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use Text for written content that
                is not a heading, a name, or a title. examples: "about me"
                sections, project descriptions, or explanations.
              </p>
              <Props
                items={[
                  {
                    name: "variant",
                    desc: "default for standard text, muted for text that is less important",
                  },
                  {
                    name: "className",
                    desc: "optional tailwind classes for custom styling",
                  },
                  {
                    name: "font",
                    desc: "cdn font url to apply a custom font (https://cdn.clove.moe/...)",
                  },
                ]}
              />
              <Code>{`<Text font="https://cdn.clove.moe/my-font.ttf" variant="muted">i love building things on the web and drinking tea</Text>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="heading" title="<Heading>">
              <p>
                shows a heading with a style. use headings to divide long
                content into parts that are easy to scan. the level prop
                controls the size - level 1 is the largest, level 3 is the
                smallest.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use Heading when a heading is not
                connected to a Section component, or when you want more control
                of the heading structure.
              </p>
              <Props
                items={[
                  {
                    name: "level",
                    desc: "1 for largest heading, 2 for medium, 3 for smallest",
                  },
                  {
                    name: "className",
                    desc: "optional tailwind classes for styling",
                  },
                  {
                    name: "font",
                    desc: "cdn font url to apply a custom font (https://cdn.clove.moe/...)",
                  },
                ]}
              />
              <Code>{`<Heading font="https://cdn.clove.moe/my-font.otf" level={2}>about me</Heading>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="link" title="<Link>">
              <p>
                makes a link that visitors can click. the link opens other
                pages, social profiles, contact methods, or external content.
                links automatically get the clove.moe style, with hover effects
                and accessibility. use clear labels that tell visitors what the
                link opens.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use Link for external content.
                usually, put Link components in a LinkGrid for social media,
                portfolios, or other important destinations.
              </p>
              <p className="text-xs">
                <strong>note:</strong> for security, only safe url schemes are
                permitted: http(s), mailto:, tel:, #fragments, or relative
                paths. the system blocks all other schemes.
              </p>
              <Props
                items={[
                  {
                    name: "href",
                    desc: "destination url - must use a safe protocol",
                  },
                  {
                    name: "className",
                    desc: "optional tailwind classes for custom styling",
                  },
                ]}
              />
              <Code>{`<Link href="https://twitter.com/yourhandle">follow me on twitter</Link>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="linkgrid" title="<LinkGrid>">
              <p>
                shows many links in a grid that is easy to scan. on a desktop,
                the links show in columns. on a mobile device, the links show
                in one vertical column. LinkGrid is the usual method to show
                your social media, portfolio, or other important destinations.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use LinkGrid when you have 2 or
                more related links, for example social media links, project
                links, or contact methods. usually, put LinkGrid in a Section.
              </p>
              <Props
                items={[
                  {
                    name: "className",
                    desc: "optional tailwind classes to adjust spacing or layout",
                  },
                ]}
              />
              <Code>{`<LinkGrid>
  <Link href="https://twitter.com/you">twitter</Link>
  <Link href="https://github.com/you">github</Link>
  <Link href="mailto:you@example.com">email</Link>
</LinkGrid>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="badge" title="<Badge>">
              <p>
                shows a small label with the shape of a pill. use badges for
                traits, interests, categories, or tags. badges are small, thus
                you can show many items in a small space. the variant prop
                controls the style.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use Badge for personality traits,
                interests, tech stack items, or other categories - for example
                "friendly", "creative", or "open source". usually, put badges
                in a BadgeGroup.
              </p>
              <Props
                items={[
                  {
                    name: "variant",
                    desc: "default for standard style, secondary for softer appearance, outline for minimal look",
                  },
                  {
                    name: "className",
                    desc: "optional tailwind classes for custom colors or styling",
                  },
                ]}
              />
              <Code>{`<Badge variant="secondary">friendly</Badge>
<Badge variant="outline">open source</Badge>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="badgegroup" title="<BadgeGroup>">
              <p>
                a container for many badges. the container sets the correct
                space between the badges. the badges automatically move to a
                new line when it is necessary. we recommend BadgeGroup for a
                set of badges. do not put the badges on the page one at a time.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use BadgeGroup when you have many
                related badges, for example interests, personality traits,
                skills, or categories.
              </p>
              <Props
                items={[
                  {
                    name: "className",
                    desc: "optional tailwind classes to adjust spacing or alignment",
                  },
                ]}
              />
              <Code>{`<BadgeGroup>
  <Badge>friendly</Badge>
  <Badge>curious</Badge>
  <Badge>creative</Badge>
  <Badge variant="outline">tea lover</Badge>
</BadgeGroup>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="skill" title="<Skill>">
              <p>
                shows a skill with an optional progress bar. the level prop (0
                to 100) controls how full the bar is. the bar gives visitors a
                quick view of your ability level. use Skill in portfolios,
                resumes, or other locations where you show your abilities.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use Skill for technical skills,
                languages, tools, or other abilities that you show as a
                percentage. usually, put skills in a SkillGrid.
              </p>
              <Props
                items={[
                  {
                    name: "level",
                    desc: "number from 0 to 100 that shows the ability level as a percentage",
                  },
                  {
                    name: "className",
                    desc: "optional tailwind classes for styling",
                  },
                ]}
              />
              <Code>{`<Skill level={85}>typescript</Skill>
<Skill level={70}>figma</Skill>`}</Code>
            </ComponentDoc>

            <ComponentDoc id="skillgrid" title="<SkillGrid>">
              <p>
                shows many Skill components in a grid that is easy to scan. on
                a desktop, the skills show in columns. on a mobile device, the
                skills show in one vertical column. visitors can see your full
                skill set quickly.
              </p>
              <p className="text-xs">
                <strong>when to use:</strong> use SkillGrid when you show many
                skills together, for example in a portfolio, a resume, or an
                "about me" section.
              </p>
              <Props
                items={[
                  {
                    name: "className",
                    desc: "optional tailwind classes to adjust grid layout or spacing",
                  },
                ]}
              />
              <Code>{`<SkillGrid>
  <Skill level={85}>typescript</Skill>
  <Skill level={70}>python</Skill>
  <Skill level={90}>react</Skill>
  <Skill level={65}>design</Skill>
</SkillGrid>`}</Code>
            </ComponentDoc>

            <div className="h-16" />
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function DocsSidebarNav({
  componentIds,
  variant = "inset",
  collapsible = "offcanvas",
}: {
  componentIds: { id: string; label: string }[];
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
}) {
  const { setOpenMobile } = useSidebar();
  const isOpen = useDocsSidebarStore((s) => s.isOpen);
  const navigate = useNavigate();

  const onAnchor = useCallback(
    (id: string) => (e: any) => {
      if (e && typeof e.preventDefault === "function") e.preventDefault();
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      // update the hash through the router with hash scrolling disabled;
      // a raw history.replaceState leaves the router re-scrolling to the
      // hash on every transition, yanking the page back as the user scrolls
      void navigate({
        hash: id,
        replace: true,
        hashScrollIntoView: false,
        resetScroll: false,
      });
      setOpenMobile(false);
    },
    [navigate, setOpenMobile]
  );

  const ICONS: Record<string, React.ComponentType<any>> = {
    bio: UserCircleIcon,
    header: Heading1Icon,
    section: SquareStackIcon,
    image: ImageIcon,
    name: TypeIcon,
    title: ItalicIcon,
    text: AlignLeftIcon,
    heading: Heading1Icon,
    link: LinkIcon,
    linkgrid: LayoutGridIcon,
    badge: TagIcon,
    badgegroup: TagsIcon,
    skill: HammerIcon,
    skillgrid: Grid2x2Icon,
  };

  const isDesktop = useMediaQuery("(min-width: 768px)");

  return (
    <Sidebar variant={variant} collapsible={collapsible}>
      <SidebarHeader>
        {!isOpen ? (
          <>
            <DocsSidebarTrigger iconSize={isDesktop ? 4 : 5} />
            <SidebarSeparator />
          </>
        ) : (
          <div className="px-2 py-2 flex items-center w-full justify-between gap-2 group-data-[collapsible=icon]:hidden">
            <img
              src="/header-logo.png"
              alt="clove.moe"
              className="h-8 w-auto group-data-[collapsible=icon]:hidden"
            />
            <DocsSidebarTrigger
              className="-ml-2"
              iconSize={isDesktop ? 4 : 5}
            />
          </div>
        )}
      </SidebarHeader>
      <SidebarContent className="overflow-y-auto overflow-x-hidden no-scrollbar">
        <SidebarGroup>
          <SidebarGroupLabel>
            <span className="group-data-[collapsible=icon]:hidden">guide</span>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="welcome">
                  <a href="#welcome" onClick={onAnchor("welcome")}>
                    <BookOpenIcon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      welcome
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="getting started">
                  <a
                    href="#getting-started"
                    onClick={onAnchor("getting-started")}
                  >
                    <RocketIcon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      getting started
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="beginner tips">
                  <a href="#tips" onClick={onAnchor("tips")}>
                    <LightbulbIcon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      beginner tips
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="using custom fonts">
                  <a href="#custom-fonts" onClick={onAnchor("custom-fonts")}>
                    <TypeIcon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      custom fonts
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="safety and moderation">
                  <a href="#safety" onClick={onAnchor("safety")}>
                    <ShieldIcon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      safety and moderation
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="faq">
                  <a href="#faq" onClick={onAnchor("faq")}>
                    <CircleHelpIcon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      faq
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                {/** keyboard shortcuts nav item removed */}
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="link transfers">
                  <a
                    href="#link-transfers"
                    onClick={onAnchor("link-transfers")}
                  >
                    <ArrowLeftRightIcon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      link transfers
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupLabel>
            <span className="group-data-[collapsible=icon]:hidden">
              bml reference
            </span>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="bml overview">
                  <a href="#bml-overview" onClick={onAnchor("bml-overview")}>
                    <BookOpenIcon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      bml overview
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="bml syntax">
                  <a href="#bml-syntax" onClick={onAnchor("bml-syntax")}>
                    <Code2Icon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      bml syntax
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="tailwind basics">
                  <a
                    href="#tailwind-basics"
                    onClick={onAnchor("tailwind-basics")}
                  >
                    <PaletteIcon />
                    <span className="group-data-[collapsible=icon]:hidden">
                      tailwind basics
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarSeparator />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>
            <span className="group-data-[collapsible=icon]:hidden">
              components
            </span>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {componentIds.map((c) => (
                <SidebarMenuItem key={c.id}>
                  <SidebarMenuButton asChild tooltip={c.label}>
                    <a href={`#${c.id}`} onClick={onAnchor(c.id)}>
                      {(() => {
                        const IconComp = ICONS[c.id] ?? FileTextIcon;
                        return <IconComp />;
                      })()}
                      <span className="group-data-[collapsible=icon]:hidden">
                        {c.label}
                      </span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}

function ComponentDoc({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t pt-10 mt-10"
    >
      <h3 className="font-mono text-lg font-semibold tracking-tight">
        {title}
      </h3>
      <div className="mt-3 text-[15px] leading-7 text-muted-foreground space-y-3">
        {children}
      </div>
    </section>
  );
}

function Props({ items }: { items: { name: string; desc: string }[] }) {
  return (
    <div className="mt-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        props
      </div>
      <div className="mt-1 divide-y divide-border/60">
        {items.map((p) => (
          <div key={p.name} className="py-2.5">
            <code className="font-mono text-[13px] font-medium text-foreground">
              {p.name}
            </code>
            <p className="mt-0.5 text-sm text-muted-foreground">{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre className="rounded-lg border bg-muted/40 p-4 text-[13px] leading-6 overflow-auto mt-2 whitespace-pre-wrap">
      {children}
    </pre>
  );
}
